import React, { useMemo } from 'react';
import moment from 'moment';

import { CreatorContainer } from '../../../containers';
import { Text } from '../../../components';
import { ActionDock, EmptyState, Screen } from '../../../design/components';
import SetupSection from '../components/SetupSection';
import SetupCreatorPlacePhotos from '../components/SetupCreatorPlacePhotos';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupAssigneePicker from '../components/SetupAssigneePicker';
import SetupPickerField from '../components/SetupPickerField';
import SetupTextField from '../components/SetupTextField';
import { MachineSelector, PlaceSelector, PlanogramSelector } from '../../../components/selector';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { POS_TYPE } from '../../../../common/constants';
import {
	getMachineSetupCardPaymentOnlyChoice,
	getMachineSetupPaymentRequirements,
	MACHINE_SETUP_CARD_PAYMENT_ONLY_HINT,
	MACHINE_SETUP_CARD_PAYMENT_ONLY_OPTIONS,
} from '../../../../common/utils/machine/machineSetupPayments';

const MachineSetupCreator = ({
	locationSetup,
	values,
	changeFormValue,
	onSubmit,
	submitting,
	errors,
}) => {
	const styles = useThemedStyles(createStyles);

	const placeFilter = useMemo(() => {
		return locationSetup?.location?.id ? { location: locationSetup.location.id } : undefined;
	}, [locationSetup?.location?.id]);

	const machineFilter = useMemo(() => {
		return { type: POS_TYPE.MACHINE };
	}, []);

	const dueDateLabel = locationSetup?.dueDate ? moment(locationSetup.dueDate).format('DD MMM YYYY') : 'Not set';

	if (!locationSetup?.id) {
		return (
			<Screen>
				<EmptyState title="Select a location setup to create a machine setup." />
			</Screen>
		);
	}

	return (
		<Screen
			heading="New machine setup"
			subheading={locationSetup.identifier || 'Location setup'}
			dock={(
				<ActionDock
					action={{
						label: 'Create machine setup',
						onPress: onSubmit,
						loading: submitting,
					}}
				/>
			)}
		>
			<SetupSection title="Location information">
				<SetupInfoRow
					label="Location"
					value={locationSetup.location?.name || 'Unknown'}
				/>
				<SetupInfoRow
					label="Client"
					value={locationSetup.location?.client?.name || 'Unknown'}
				/>
				<SetupInfoRow
					label="Due date"
					value={dueDateLabel}
				/>
			</SetupSection>

			<SetupSection title="Machine details">
				<Text style={styles.label}>Place</Text>
				<PlaceSelector
					value={values.place}
					onChange={(place) => changeFormValue({ place })}
					initialFilter={placeFilter}
					buttonStyle={styles.selectorButton}
				/>

				<SetupCreatorPlacePhotos
					photos={values.placePhotos}
					onChange={placePhotos => changeFormValue({ placePhotos })}
					hint="Insert photos of the place where the machine will go."
					error={errors?.placePhotos}
				/>

				<Text style={styles.label}>Machine</Text>
				<MachineSelector
					value={values.machine}
					onChange={(machine) => changeFormValue({ machine })}
					initialFilter={machineFilter}
					buttonStyle={styles.selectorButton}
				/>

				<Text style={styles.label}>Planogram (optional)</Text>
				<PlanogramSelector
					value={values.planogram}
					onChange={(planogram) => changeFormValue({ planogram })}
					buttonStyle={styles.selectorButton}
				/>

				<SetupTextField
					label="Additional instructions"
					value={values.additionalInstructions}
					field="additionalInstructions"
					onConfirm={changeFormValue}
					multiline={true}
					numberOfLines={4}
					placeholder="Add any context for the team"
				/>
			</SetupSection>

			<SetupSection title="Payment system">
				<SetupPickerField
					label="Card payment only machine?"
					value={getMachineSetupCardPaymentOnlyChoice(values)}
					onChange={(choice) => {
						const requirements = getMachineSetupPaymentRequirements(choice);
						if (requirements) {
							changeFormValue(requirements);
						}
					}}
					options={MACHINE_SETUP_CARD_PAYMENT_ONLY_OPTIONS}
					hint={MACHINE_SETUP_CARD_PAYMENT_ONLY_HINT}
					required={true}
				/>
			</SetupSection>

			<SetupSection title="Assignees">
				<Text style={styles.label}>Assign users to this setup</Text>
				<SetupAssigneePicker
					assignees={values.assignees || []}
					onChange={(assignees) => changeFormValue({ assignees })}
					returnObjects={true}
					placeholder="Add assignee"
				/>
			</SetupSection>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	label: {
		...type.fieldLabel,
		color: colors.text,
		marginBottom: 6,
		marginTop: space.md,
	},
	selectorButton: {
		marginBottom: space.sm,
	},
});

export default CreatorContainer(MachineSetupCreator);
